/*
 * Copyright (c) 2023 - 2026 Bluemate B.V.
 */

import {getMode} from '@dvrd/dvr-controls';
import {ProductSelectionClauseType} from './types';

export const NO_REPLY_FLEX_KEY = 'use_no_reply_tracking';
export const ACCOUNT_PROGRESS_FLEX_KEY = 'account_setup_progress';
export const VAT_BETA_FLEX_KEY = 'vat_declaration_beta';
export const SELECTED_ACCOUNT_KEY = 'bluemate-selected-account';
export const SENDING_HISTORY_MAILS_KEY = 'sending_history_mails';
export const SURVEY_CHOICES_KEY = 'survey_choices';
export const COLOR_BLUE_BLUEMATE = '#2568EF';
export const COLOR_GRAY_8 = '#D5D5D5';
export const ISO_DATE_FORMAT = 'YYYY-MM-DD';
export const DUTCH_DATE_FORMAT = 'DD-MM-YYYY';
export const DUTCH_DATE_TIME_FORMAT = `${DUTCH_DATE_FORMAT} HH:mm`;
export const BASIC_MESSAGES_KEY = 'basic_messages';
export const MONTH_LABELS = [
    'januari',
    'februari',
    'maart',
    'april',
    'mei',
    'juni',
    'juli',
    'augustus',
    'september',
    'oktober',
    'november',
    'december'
];
export const MULTI_LOGIN_BETA_KEY = 'multi-login-beta';
export const LAST_REVIEW_FLEX_KEY = 'review_feed_last_seen';
export const DEFAULT_PRODUCT_SELECTION: ProductSelectionClauseType = {clause_type: 'include', selection_type: 'all'};
export const OAUTH_TYPE_KEY = 'oauthType';

export const OAUTH_SEARCH_RANKINGS_REDIRECT = '76279da34e1c4afda7b5ba34928da098';
export const OAUTH_SALES_DASHBOARD_REDIRECT = '4f0dc7d6191a483285cd6a3204133382';

export const REGISTER_PATH = 'registreren';
export const REGISTER_ROUTE = `/${REGISTER_PATH}`;
export const LOGIN_PATH = 'inloggen';
export const LOGIN_ROUTE = `/${LOGIN_PATH}`;
export const DEMO_PATH = 'demo';
export const DEMO_ROUTE = `/${DEMO_PATH}`;
export const FORGOT_PASSWORD_PATH = 'wachtwoord-vergeten';
export const FORGOT_PASSWORD_ROUTE = `/${FORGOT_PASSWORD_PATH}`;
export const RESET_PASSWORD_PATH = 'wachtwoord-resetten/:code';
export const RESET_PASSWORD_ROUTE = (code: string) => `/${RESET_PASSWORD_PATH}/${code}`;
export const TWO_FACTOR_RESET_PATH = '2fa-reset/:token';
export const TWO_FACTOR_RESET_ROUTE = (token: string) => `/${TWO_FACTOR_RESET_PATH}/${token}`;
export const PRICE_CALCULATOR_PATH = 'prijs-calculator';
export const PRICE_CALCULATOR_ROUTE = `/${PRICE_CALCULATOR_PATH}`;
export const MAINTENANCE_PATH = 'onderhoud';
export const MAINTENANCE_ROUTE = `/${MAINTENANCE_PATH}`;
export const INVOICES_PATH = 'facturen';
export const INVOICES_ROUTE = `/${INVOICES_PATH}`;
export const ORDERS_PATH = 'orders';
export const ORDERS_ROUTE = `${INVOICES_ROUTE}/${ORDERS_PATH}`;
export const INVOICE_REQUESTS_PATH = 'aanvragen';
export const INVOICE_REQUESTS_ROUTE = `${INVOICES_ROUTE}/${INVOICE_REQUESTS_PATH}`;
export const INVOICE_DETAILS_PATH = 'factuur-details';
export const INVOICE_DETAILS_ROUTE = `/${INVOICE_DETAILS_PATH}`;
export const EDIT_INVOICE_PATH = 'factuur-wijzigen';
export const EDIT_INVOICE_ROUTE = `/${EDIT_INVOICE_PATH}`;
export const PDF_LAYOUT_PATH = 'factuur-layout';
export const PDF_LAYOUT_ROUTE = `/${PDF_LAYOUT_PATH}`;
export const CONNECTION_COMPLETE_PATH = 'koppeling-geslaagd';
export const CONNECTION_COMPLETE_ROUTE = `/${CONNECTION_COMPLETE_PATH}`;
export const REVIEW_ANALYTICS_PATH = 'review-analytics';
export const REVIEW_ANALYTICS_ROUTE = `/${REVIEW_ANALYTICS_PATH}`;
export const QR_CODES_PATH = 'qr-codes';
export const QR_CODES_ROUTE = `/${QR_CODES_PATH}`;
export const PRODUCTS_PATH = 'producten';
export const PRODUCTS_ROUTE = `/${PRODUCTS_PATH}`;
export const PRODUCT_REVIEWS_PATH = 'product-reviews/:ean';
export const BOL_REVIEWS_URL_FRAGMENT = '#:~:text=Reviews,-Schrijf%20review&text=Reviews,-Nog%20geen%20reviews';
export const PRODUCT_REVIEWS_ROUTE = (ean: string) => `/${PRODUCT_REVIEWS_PATH}/${ean}`;
export const SEARCH_TRENDS_PATH = 'zoek-trends';
export const SEARCH_VOLUMES_PATH = 'zoekvolume';
export const SEARCH_VOLUMES_ROUTE = `/${SEARCH_VOLUMES_PATH}`;
export const SALES_DASHBOARD_PATH = 'sales-dashboard';
export const SALES_DASHBOARD_ROUTE = `/${SALES_DASHBOARD_PATH}`;
export const SALES_DASHBOARD_V2_PATH = 'sales-dashboard-v2';
export const SALES_DASHBOARD_V2_ROUTE = `/${SALES_DASHBOARD_V2_PATH}`;
export const SEARCH_RANKINGS_PATH = 'search-rankings';
export const SEARCH_RANKINGS_ROUTE = `/${SEARCH_RANKINGS_PATH}`;
export const MOBILE_APP_PATH = 'mobiele-app';
export const MOBILE_APP_ROUTE = `/${MOBILE_APP_PATH}`;
// Deep link used by the Android banner that appears when the browser withholds
// its one-tap install: opens the guide on the Android tab, scrolled to the
// browser-menu route — the only one that works in that state.
export const MOBILE_APP_INSTALL_PARAM = 'install';
export const MOBILE_APP_INSTALL_MENU = 'menu';
export const MOBILE_APP_MENU_INSTALL_ROUTE = `${MOBILE_APP_ROUTE}?${MOBILE_APP_INSTALL_PARAM}=${MOBILE_APP_INSTALL_MENU}`;
export const CHROME_EXTENSION_PATH = 'chrome-extensie';
export const CHROME_EXTENSION_ROUTE = `/${CHROME_EXTENSION_PATH}`;
export const CHROME_EXTENSION_STORE_URL =
    'https://chromewebstore.google.com/detail/bolmate-gratis-data-inzic/cfndneealmlhpllohooglljaipepeeed';
export const MCP_SERVER_PATH = 'mcp';
export const MCP_SERVER_ROUTE = `/${MCP_SERVER_PATH}`;
export const mcpServerUrl = (): string => (getMode() === 'dev' ? 'http://localhost:1338/' : 'https://mcp.bluemate.nl/');
export const SETTINGS_PATH = 'instellingen';
export const SETTINGS_ROUTE = `/${SETTINGS_PATH}`;
export const COMMON_SETTINGS_PATH = 'algemeen';
export const COMMON_SETTINGS_ROUTE = `${SETTINGS_ROUTE}/${COMMON_SETTINGS_PATH}`;
export const ACCESS_SETTINGS_PATH = 'gebruikers';
export const ACCESS_SETTINGS_ROUTE = `${SETTINGS_ROUTE}/${ACCESS_SETTINGS_PATH}`;
export const SECURITY_SETTINGS_PATH = 'beveiliging';
export const SECURITY_SETTINGS_ROUTE = `${SETTINGS_ROUTE}/${SECURITY_SETTINGS_PATH}`;
export const API_SETTINGS_PATH = 'koppeling';
export const API_SETTINGS_ROUTE = `${SETTINGS_ROUTE}/${API_SETTINGS_PATH}`;
export const INVOICE_SETTINGS_PATH = 'factuur-instellingen';
export const INVOICE_SETTINGS_ROUTE = `${SETTINGS_ROUTE}/${INVOICE_SETTINGS_PATH}`;
export const PRODUCT_SETTINGS_PATH = 'producten';
export const PRODUCT_SETTINGS_ROUTE = `${SETTINGS_ROUTE}/${PRODUCT_SETTINGS_PATH}`;
export const SUBSCRIPTION_PATH = 'abonnement';
export const SUBSCRIPTION_ROUTE = `${SETTINGS_ROUTE}/${SUBSCRIPTION_PATH}`;
export const EXPORT_PATH = 'exporteren';
export const EXPORT_ROUTE = `${SETTINGS_ROUTE}/${EXPORT_PATH}`;
export const EMAIL_SETTINGS_PATH = 'emails';
export const EMAIL_SETTINGS_ROUTE = `${SETTINGS_ROUTE}/${EMAIL_SETTINGS_PATH}`;
export const SETUP_ACCOUNT_PATH = 'account-instellen';
export const SETUP_ACCOUNT_ROUTE = `/${SETUP_ACCOUNT_PATH}`;
export const CREDIT_DATA_PATH = 'credit-data';
export const CREDIT_DATA_ROUTE = `/${CREDIT_DATA_PATH}`;
export const IDEAL_PATH = 'ideal';
export const IDEAL_ROUTE = `/${IDEAL_PATH}`;
export const IDEAL_COMPLETE_PATH = 'ideal-complete';
export const IDEAL_COMPLETE_ROUTE = `/${IDEAL_COMPLETE_PATH}`;
export const EMAILS_PATH = 'emails';
export const EMAIL_SEQUENCE_PATH = 'email-volgorde';
export const EMAILS_ROUTE = `/${EMAILS_PATH}`;
export const EMAIL_TEMPLATE_PATH = 'template-editor';
export const EMAIL_TEMPLATE_ROUTE = `/${EMAIL_TEMPLATE_PATH}`;
export const RETURNS_PATH = 'retouren';
export const RETURNS_ROUTE = `/${RETURNS_PATH}`;
export const VAT_DECLARATION_PATH = 'btw-aangifte';
export const VAT_DECLARATION_ROUTE = `/${VAT_DECLARATION_PATH}`;
export const UNSUBSCRIBE_MONTHLY_PATH = 'uitschrijven-maandelijkse-link/:userId';
export const UNSUBSCRIBE_MONTHLY_ROUTE = (userID: string) => `/${UNSUBSCRIBE_MONTHLY_PATH}/${userID}`;
export const ADMIN_PATH = 'admin';
export const ADMIN_ROUTE = `/${ADMIN_PATH}`;
export const ADMIN_USER_PATH = 'gebruiker-details';
export const ADMIN_USER_ROUTE = `${ADMIN_ROUTE}/${ADMIN_USER_PATH}`;
export const ADMIN_ACCOUNT_PATH = 'account-details';
export const ADMIN_ACCOUNT_ROUTE = `${ADMIN_ROUTE}/${ADMIN_ACCOUNT_PATH}`;
export const ADMIN_THREADS_PATH = 'threads';
export const ADMIN_THREADS_ROUTE = `${ADMIN_ROUTE}/${ADMIN_THREADS_PATH}`;
export const ADMIN_USERS_PATH = 'gebruikers';
export const ADMIN_USERS_ROUTE = `${ADMIN_ROUTE}/${ADMIN_USERS_PATH}`;
export const ADMIN_SERVICES_PATH = 'services';
export const ADMIN_SERVICES_ROUTE = `${ADMIN_ROUTE}/${ADMIN_SERVICES_PATH}`;
export const ADMIN_IMPORT_PATH = 'historie-import';
export const ADMIN_IMPORT_ROUTE = `${ADMIN_ROUTE}/${ADMIN_IMPORT_PATH}`;
export const ADMIN_EMAILS_PATH = 'email-verzending';
export const ADMIN_EMAILS_ROUTE = `${ADMIN_ROUTE}/${ADMIN_EMAILS_PATH}`;
export const ADMIN_PAYMENTS_PATH = 'mislukte-betalingen';
export const ADMIN_PAYMENTS_ROUTE = `${ADMIN_ROUTE}/${ADMIN_PAYMENTS_PATH}`;
export const ADMIN_USERS_SELECT_PATH = 'gebruikers-selecteren';
export const ADMIN_USERS_SELECT_ROUTE = `${ADMIN_ROUTE}/${ADMIN_USERS_SELECT_PATH}`;
export const ADMIN_DEFAULT_TEMPLATES_PATH = 'admin-default-templates';
export const ADMIN_DEFAULT_TEMPLATES_ROUTE = `${ADMIN_ROUTE}/${ADMIN_DEFAULT_TEMPLATES_PATH}`;
export const ADMIN_TEMPLATE_EDITOR_PATH = 'admin-template-editor';
export const ADMIN_TEMPLATE_EDITOR_ROUTE = (templateID: string | null) =>
    `${ADMIN_ROUTE}/${ADMIN_TEMPLATE_EDITOR_PATH}?template=${templateID ?? ''}`;
export const OAUTH_PATH = 'oauth/bol';
export const OAUTH_ROUTE = `/${OAUTH_PATH}`;
export const OAUTH_INSIGHTS_PATH = 'oauth/bol-data-insights';
export const OAUTH_INSIGHTS_ROUTE = `/${OAUTH_INSIGHTS_PATH}`;
export const OAUTH_AUTHORIZE_PATH = 'oauth/authorize';
export const OAUTH_AUTHORIZE_ROUTE = `/${OAUTH_AUTHORIZE_PATH}`;
export const REFER_A_FRIEND_PATH = 'refer-a-friend';
export const REFER_A_FRIEND_ROUTE = `/${REFER_A_FRIEND_PATH}`;
export const NOTIFICATIONS_PATH = 'notificaties';
export const NOTIFICATIONS_ROUTE = `/${NOTIFICATIONS_PATH}`;
export const DASHBOARD_PATH = 'overzicht';
export const DASHBOARD_ROUTE = `/${DASHBOARD_PATH}`;
export const PEPPOL_INVOICES_PATH = 'peppol';
export const PEPPOL_INVOICES_ROUTE = `${INVOICES_ROUTE}/${PEPPOL_INVOICES_PATH}`;
export const ADMIN_PLATFORM_USAGE_PATH = 'platformgebruik';
export const ADMIN_PLATFORM_USAGE_ROUTE = `${ADMIN_ROUTE}/${ADMIN_PLATFORM_USAGE_PATH}`;

// Routes where the "subscription paused" paywall must NOT block the page. Besides settings and admin
// (a paused user starts a subscription / exports invoices there), these cover the standalone flows a
// paused user still has to finish: the iDEAL payment form + its Stripe return, and the Bol connection
// callback + success page. The paywall mounts globally in app.tsx, so without these exemptions it
// would cover e.g. the iDEAL form and make paying with iDEAL impossible.
// The mobile-app guide is exempt too: it is a static install manual with no gated data, and every
// customer must be able to install the app (and resume from it) regardless of subscription state.
// The OAuth authorize page is exempt for the same reason as the Bol callback: it is a handoff the
// user was sent to by another application, and the paywall dialog would strand that flow instead of
// returning an answer to the waiting client.
export const PAYWALL_EXEMPT_ROUTES = [
    SETTINGS_ROUTE,
    ADMIN_ROUTE,
    IDEAL_ROUTE,
    IDEAL_COMPLETE_ROUTE,
    OAUTH_ROUTE,
    MOBILE_APP_ROUTE,
    OAUTH_INSIGHTS_ROUTE,
    OAUTH_AUTHORIZE_ROUTE,
    CONNECTION_COMPLETE_ROUTE
];

export const isPaywallExemptPath = (pathname: string): boolean =>
    PAYWALL_EXEMPT_ROUTES.some(route => pathname.startsWith(route));

// Standalone views rendered without the app chrome even when a session exists:
// the MCP connect hand-off must never look like the app, and the price
// calculator is a public embed.
export const BARE_ROUTES = [OAUTH_AUTHORIZE_ROUTE, PRICE_CALCULATOR_ROUTE];

export const isBareRoutePath = (pathname: string): boolean => BARE_ROUTES.some(route => pathname.startsWith(route));

// Single source of truth for "this page has a mobile layout": drives both the
// "not optimized for mobile" warning banner (app.tsx) and the mobile-friendly
// marker in the menu (sidebarMenu). A page becomes mobile-friendly everywhere
// the moment its route is added here.
export const MOBILE_READY_ROUTES = [
    DASHBOARD_ROUTE,
    ORDERS_ROUTE,
    NOTIFICATIONS_ROUTE,
    REVIEW_ANALYTICS_ROUTE,
    INVOICE_DETAILS_ROUTE,
    EDIT_INVOICE_ROUTE,
    CREDIT_DATA_ROUTE,
    MOBILE_APP_ROUTE,
    CHROME_EXTENSION_ROUTE,
    MCP_SERVER_ROUTE,
    ADMIN_ROUTE
];

export const isMobileReadyRoute = (route: unknown): boolean =>
    typeof route === 'string' && MOBILE_READY_ROUTES.some(r => route === r || route.startsWith(`${r}/`));
